import {
  useEffect, useRef, useState,
} from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import StatCard from './StatCard';
import { homeColors, homeEase } from '../style/homeStyles';

const CARD_WIDTH = 200;
const CARD_GAP = 12;
const STEP_WIDTH = CARD_WIDTH + CARD_GAP;
// Se cambia de tarjeta al arrastrar un 30% del paso, o con un flick rápido (px/ms).
const DRAG_RATIO = 0.3;
const FLICK_VELOCITY = 0.4;

const StatsCarousel = ({ stats }) => {
  const stageRef = useRef(null);
  const trackRef = useRef(null);
  const drag = useRef({
    active: false, startX: 0, lastX: 0, lastTime: 0, velocity: 0,
  });

  const [page, setPage] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);

  useEffect(() => {
    if (!drag.current.active) setDragX(0);
  }, [page]);

  // Medimos el ancho útil para que la última tarjeta quede alineada con el borde derecho.
  useEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      const track = trackRef.current;
      if (!stage || !track) return;
      const styles = getComputedStyle(stage);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const inner = stage.clientWidth - paddingLeft - paddingRight;
      setMaxOffset(Math.max(0, track.scrollWidth - inner));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stats.length]);

  const maxPage = Math.min(stats.length - 1, Math.ceil(maxOffset / STEP_WIDTH));

  const handlePointerDown = (event) => {
    drag.current = {
      active: true, startX: event.clientX, lastX: event.clientX, lastTime: performance.now(), velocity: 0,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!drag.current.active) return;
    const now = performance.now();
    const elapsed = Math.max(1, now - drag.current.lastTime);
    drag.current.velocity = (event.clientX - drag.current.lastX) / elapsed;
    drag.current.lastX = event.clientX;
    drag.current.lastTime = now;
    setDragX(event.clientX - drag.current.startX);
  };

  const handlePointerUp = (event) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // El puntero ya se liberó (por ejemplo al salir de la ventana): no hay nada que hacer.
    }

    const distance = drag.current.lastX - drag.current.startX;
    const { velocity } = drag.current;
    let next = page;
    if (distance < -STEP_WIDTH * DRAG_RATIO || velocity < -FLICK_VELOCITY) next = Math.min(maxPage, page + 1);
    else if (distance > STEP_WIDTH * DRAG_RATIO || velocity > FLICK_VELOCITY) next = Math.max(0, page - 1);

    setPage(next);
    setDragX(0);
  };

  const baseX = Math.max(-maxOffset, -page * STEP_WIDTH);
  let translateX = baseX + dragX;
  if (drag.current.active) {
    // Efecto elástico al pasarse de los límites.
    if (translateX > 0) translateX *= 0.35;
    else if (translateX < -maxOffset) translateX = -maxOffset + (translateX + maxOffset) * 0.35;
  } else {
    translateX = Math.min(0, Math.max(-maxOffset, translateX));
  }

  return (
    <Box
      ref={stageRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: 'relative',
        margin: '14px -20px 0',
        padding: '8px 20px 4px',
        overflow: 'hidden',
        touchAction: 'pan-y',
        cursor: 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box
        ref={trackRef}
        sx={{
          display: 'flex',
          gap: `${CARD_GAP}px`,
          willChange: 'transform',
          transform: `translateX(${translateX}px)`,
          transition: drag.current.active ? 'none' : `transform .55s ${homeEase}`,
        }}
      >
        {stats.map((stat, index) => (
          <Box
            key={stat.id}
            sx={{
              flex: `0 0 ${CARD_WIDTH}px`,
              opacity: page === index ? 1 : 0.55,
              transform: page === index ? 'scale(1)' : 'scale(0.96)',
              transition: `opacity .35s ${homeEase}, transform .55s ${homeEase}`,
            }}
          >
            <StatCard value={stat.value} label={stat.label} />
          </Box>
        ))}
      </Box>

      <Box sx={{
        display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '18px',
      }}
      >
        {stats.slice(0, maxPage + 1).map((stat, index) => (
          <Box
            key={stat.id}
            component="button"
            type="button"
            onClick={() => setPage(index)}
            aria-label={stat.value}
            sx={{
              width: page === index ? 22 : 6,
              height: 6,
              padding: 0,
              border: 'none',
              borderRadius: '999px',
              cursor: 'pointer',
              backgroundColor: page === index ? homeColors.pink : 'rgba(31,27,29,0.15)',
              transition: `width .35s ${homeEase}, background-color .25s ${homeEase}`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

StatsCarousel.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
};

export default StatsCarousel;
